<script setup>
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableRowRounded from "@/components/global-themed/table/TableRowRounded.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import { useKitPages } from "@/composables/kit-pages/useKitPages";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import useUser from "@/composables/useUser";
import TooltipQuestion from "@/components/global-themed/TooltipQuestion.vue";

const props = defineProps({
  websiteId: {
    type: Number,
    required: true,
  },
});

const { kitPages, isPending, implementFixesMutation } = useKitPages(props.websiteId);
const {isDeveloper} = useUser();

const {mutate: implementFixes } = implementFixesMutation();
</script>

<template>
  <DataTable class="tw-w-full">
    <TableHeaderRow class="tw-grid-cols-16">
      <TableHeaderCell col-span="6">
        <span>Page Url</span>
      </TableHeaderCell>

      <TableHeaderCell col-span="3">
        <span class="tw-flex tw-gap-2 tw-items-center">
          Available fixes count
          <TooltipQuestion
            text="This shows the number of actions Kit can fix, not the total number of actions. That’s why it may differ from the Actions Overview."
          />
        </span>
      </TableHeaderCell>

      <TableHeaderCell col-span="3">
        <span>Keywords count</span>
      </TableHeaderCell>

      <TableHeaderCell col-span="4" />
    </TableHeaderRow>

    <div
      v-if="isPending"
      class="tw-bg-white tw-p-8 tw-rounded-lg"
    >
      <LoadingBlock />
    </div>

    <div
      v-else
      class="tw-flex tw-flex-col tw-gap-4 tw-text-neutral-900 tw-text-base tw-mt-8"
    >
      <ZeroContent v-if="kitPages?.length < 1">
        Looks like there are no pages with Kit fixes available.
      </ZeroContent>

      <TableRowRounded
        v-for="page in kitPages"
        :key="page.id"
        class="tw-grid-cols-16"
      >
        <TableCell
          col-span="6"
          class="tw-flex tw-items-center tw-gap-4"
        >
          {{ page.page_url }}
        </TableCell>

        <TableCell col-span="3">
          {{ page.kit_fixable_actions_count }}
        </TableCell>

        <TableCell col-span="3">
          {{ page.keywords_count }}
        </TableCell>

        <TableCell col-span="4">
          <div class="tw-flex tw-items-center tw-gap-4 tw-justify-end">
            <svg-vue
              v-if="page.kit_schedule_id"
              v-tooltip="{
                content: 'Kit has previously optimised this page.',
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
              }"
              class="tw-w-8 tw-text-success-700 tw-cursor-pointer"
              icon="icons/complete"
            />

            <DefaultButton
              v-if="isDeveloper"
              size="small"
              @click="implementFixes(page.id)"
            >
              Implement fixes
            </DefaultButton>
          </div>
        </TableCell>
      </TableRowRounded>
    </div>
  </DataTable>
</template>
